import { Flame, AlertTriangle } from 'lucide-react';
import { useRankingStore } from '@/shared/stores/ranking.store';
import { useAuth } from '@features/auth';

interface StreakWidgetProps {
  size?: 'sm' | 'md';
  showTooltip?: boolean;
}

/**
 * Compact streak widget to display in header/navbar.
 * Shows current streak with visual urgency if at risk.
 */
export function StreakWidget({ size = 'md', showTooltip = true }: StreakWidgetProps) {
  const { user } = useAuth();
  const streak = useRankingStore((state) => state.streak);

  // Only show for students
  if (user?.role !== 'student' || !streak) {
    return null;
  }

  const { currentStreak, hasActivityToday, daysUntilNextMilestone, milestone } = streak;
  const isAtRisk = !hasActivityToday && currentStreak > 0;

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs gap-1',
    md: 'px-3 py-1.5 text-sm gap-1.5',
  };

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <div className="relative group">
      <div
        className={`
          flex items-center rounded-full font-semibold transition-all
          ${sizeClasses[size]}
          ${isAtRisk
            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 animate-pulse'
            : currentStreak > 0
              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
          }
        `}
      >
        {isAtRisk ? (
          <AlertTriangle className={`${iconSize} animate-bounce`} />
        ) : (
          <Flame className={`${iconSize} ${currentStreak > 0 ? 'text-orange-500' : ''}`} />
        )}
        <span>{currentStreak}</span>
      </div>

      {/* Tooltip - Aligned right to prevent overflow */}
      {showTooltip && (
        <div className="absolute top-full right-0 mt-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
            {isAtRisk ? (
              <div className="text-center">
                <p className="font-semibold text-orange-400">¡Racha en riesgo!</p>
                <p className="text-gray-300">Completa una lección para mantenerla</p>
              </div>
            ) : currentStreak > 0 ? (
              <div className="text-center">
                <p className="font-semibold">{currentStreak} días de racha 🔥</p>
                {milestone && daysUntilNextMilestone > 0 && (
                  <p className="text-gray-300">
                    {daysUntilNextMilestone} días para el hito de {milestone}
                  </p>
                )}
                {hasActivityToday && (
                  <p className="text-green-400 text-[10px] mt-1">✓ Ya estudiaste hoy</p>
                )}
              </div>
            ) : (
              <div className="text-center">
                <p>Sin racha activa</p>
                <p className="text-gray-300">¡Comienza hoy!</p>
              </div>
            )}
            {/* Arrow - positioned right to match tooltip alignment */}
            <div className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
}
